import {useMemo, useState} from 'react';
import {format} from 'date-fns';
import {Link, useNavigate} from 'react-router-dom';

import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {
  DataTable,
  useDataTable,
  useResetPageOnChange,
  type Column,
} from '@common-web/components/data-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {ExportCsvButton} from '@common-web/components/export-csv-button';

import {
  Plus,
  MessageSquare,
  FileText,
  Eye,
  Calendar,
  Building,
  Search,
  UserPlus,
  Send,
} from 'lucide-react';

import {
  getProspectStageDisplayName,
  getProspectStageColor,
  getProspectStageIcon,
} from '@common-web/components/prospect/ProspectStatus';
import {useAuthStore} from '@common-web/stores/authStore';
import {RouterOutput, trpc} from '@common-web/lib/trpc';
import {
  ProspectStage,
  PROSPECT_STAGE_ORDER,
  PROSPECT_STAGE_DISPLAY_NAMES,
} from '@common/types/prospect';
import {MS_PER_DAY} from '@common/constants/time';

type ProspectRow = RouterOutput['clients']['prospects']['list']['data'][number];
type Filters = {stage: string; client: string};

const KANBAN_STAGES = [
  ProspectStage.NEW_LEAD,
  ProspectStage.COMMUNICATING,
  ProspectStage.PROPOSAL_SENT,
  ProspectStage.PROPOSAL_PRESENTATION,
];

interface ProspectPipelineProps {
  viewMode: 'table' | 'kanban';
  createUrl: string;
  /** Broker visibility scope ('team' includes the broker's downline). */
  scope?: 'mine' | 'team';
  /** When in team scope, narrow to a single downline broker. */
  assignedBrokerId?: string | null;
}

export function ProspectPipeline({
  viewMode,
  createUrl,
  scope,
  assignedBrokerId,
}: ProspectPipelineProps) {
  const navigate = useNavigate();
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const isBrokerUser = useAuthStore((state) => state.isBrokerUser());

  // Prefixed URL keys so this table coexists with the deals/archive tables in
  // the pipeline dashboard tabs.
  const table = useDataTable<Filters>({
    defaultPageSize: 20,
    defaultFilters: {stage: '', client: ''},
    searchDebounce: 900,
    syncToUrl: 'prospects',
  });

  const stageFilter = table.filterValues.stage;
  const clientFilter = table.filterValues.client;
  const pageSize = viewMode === 'kanban' ? 200 : table.pageSize;

  const [kanbanDragTarget, setKanbanDragTarget] = useState<{
    prospectId: string;
    prospectName: string;
    targetStage: ProspectStage;
  } | null>(null);

  const prospectsQuery = trpc.clients.prospects.list.useQuery({
    stage: stageFilter ? (stageFilter as ProspectStage) : undefined,
    search: table.debouncedSearch || undefined,
    page: table.page,
    limit: pageSize,
    clientId: isInternalUser && clientFilter ? clientFilter : undefined,
    scope,
    assignedBrokerId: assignedBrokerId ?? undefined,
  });

  const clientsQuery = trpc.internal.client.list.useQuery(undefined, {
    enabled: isInternalUser,
    refetchOnWindowFocus: false,
  });

  const updateStageMutation = trpc.clients.prospects.updateStage.useMutation({
    onSuccess: () => {
      prospectsQuery.refetch();
    },
  });

  const exportCsvQuery = trpc.clients.prospects.exportCsv.useQuery(
    {
      stage: stageFilter ? (stageFilter as ProspectStage) : undefined,
      search: table.debouncedSearch || undefined,
      clientId: isInternalUser && clientFilter ? clientFilter : undefined,
      scope,
      assignedBrokerId: assignedBrokerId ?? undefined,
    },
    {enabled: false},
  );

  // Filter/search changes reset the page inside useDataTable; view-mode and
  // broker-scope changes live outside it.
  useResetPageOnChange(`${viewMode}|${scope ?? ''}|${assignedBrokerId ?? ''}`, table);

  const data = prospectsQuery.data?.data || [];
  const pagination = prospectsQuery.data?.pagination;
  const stageCounts = prospectsQuery.data?.stageCounts || {};

  const groupedByStage = useMemo(() => {
    const groups: Record<string, typeof data> = {};
    KANBAN_STAGES.forEach((s) => (groups[s] = []));
    data.forEach((item) => {
      if (groups[item.stage]) {
        groups[item.stage].push(item);
      }
    });
    return groups;
  }, [data]);

  const getDetailUrl = (id: string) =>
    isBrokerUser ? `/broker/prospects/${id}` : `/prospects/${id}`;

  const getDaysInStage = (prospect: {
    stage: string;
    newLeadAt: string;
    communicatingAt: string | null;
    proposalSentAt: string | null;
    proposalPresentationAt: string | null;
  }) => {
    const tsMap: Record<string, string | null> = {
      NEW_LEAD: prospect.newLeadAt,
      COMMUNICATING: prospect.communicatingAt,
      PROPOSAL_SENT: prospect.proposalSentAt,
      PROPOSAL_PRESENTATION: prospect.proposalPresentationAt,
    };
    const ts = tsMap[prospect.stage];
    if (!ts) return 0;
    return Math.floor((Date.now() - new Date(ts).getTime()) / MS_PER_DAY);
  };

  const hasActiveFilters = !!table.debouncedSearch || !!stageFilter;

  const columns: Column<ProspectRow>[] = [
    {
      id: 'company',
      header: 'Company',
      render: (prospect) => (
        <div className='flex items-center gap-2'>
          <Building className='h-4 w-4 text-brand-gray' />
          <div>
            <p className='font-medium text-brand-navy'>{prospect.companyName}</p>
            {isInternalUser && <p className='text-sm text-brand-gray'>{prospect.client.name}</p>}
          </div>
        </div>
      ),
    },
    {
      id: 'state',
      header: 'State',
      className: 'text-sm',
      render: (prospect) => prospect.state || '\u2014',
    },
    {
      id: 'employees',
      header: 'Est. Employees',
      className: 'text-sm',
      render: (prospect) => (
        <>
          {prospect.estimatedFtEmployees != null ? `${prospect.estimatedFtEmployees} FT` : ''}
          {prospect.estimatedFtEmployees != null && prospect.estimatedPtEmployees != null && ' / '}
          {prospect.estimatedPtEmployees != null ? `${prospect.estimatedPtEmployees} PT` : ''}
          {prospect.estimatedFtEmployees == null &&
            prospect.estimatedPtEmployees == null &&
            '\u2014'}
        </>
      ),
    },
    {
      id: 'stage',
      header: 'Stage',
      render: (prospect) => (
        <div className='flex items-center gap-2'>
          {getProspectStageIcon(prospect.stage as ProspectStage)}
          <Badge variant={getProspectStageColor(prospect.stage as ProspectStage)}>
            {getProspectStageDisplayName(prospect.stage as ProspectStage)}
          </Badge>
        </div>
      ),
    },
    {
      id: 'broker',
      header: 'Broker',
      className: 'text-sm',
      render: (prospect) => prospect.assignedBroker?.name || '\u2014',
    },
    {
      id: 'daysInStage',
      header: 'Days in Stage',
      className: 'text-sm',
      render: (prospect) => `${getDaysInStage(prospect as never)}d`,
    },
    {
      id: 'created',
      header: 'Created',
      render: (prospect) => (
        <div className='flex items-center gap-2 text-sm text-brand-gray'>
          <Calendar className='h-3 w-3' />
          {format(new Date(prospect.createdAt), 'MMM d, yyyy')}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (prospect) => (
        <Button variant='outline' size='sm' onClick={() => navigate(getDetailUrl(prospect.id))}>
          <Eye className='h-4 w-4 mr-1' />
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* Metric Cards */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
        <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
          <CardHeader className='pb-3'>
            <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
              New Leads
              <UserPlus className='h-5 w-5 text-blue-500' />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {prospectsQuery.isLoading ? (
              <Skeleton className='h-8 w-20' />
            ) : (
              <div className='text-3xl font-bold text-brand-navy'>
                {stageCounts[ProspectStage.NEW_LEAD] || 0}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
          <CardHeader className='pb-3'>
            <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
              Communicating
              <MessageSquare className='h-5 w-5 text-amber-500' />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {prospectsQuery.isLoading ? (
              <Skeleton className='h-8 w-20' />
            ) : (
              <div className='text-3xl font-bold text-brand-navy'>
                {stageCounts[ProspectStage.COMMUNICATING] || 0}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
          <CardHeader className='pb-3'>
            <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
              Proposals
              <Send className='h-5 w-5 text-purple-500' />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {prospectsQuery.isLoading ? (
              <Skeleton className='h-8 w-20' />
            ) : (
              <div className='text-3xl font-bold text-brand-navy'>
                {(stageCounts[ProspectStage.PROPOSAL_SENT] || 0) +
                  (stageCounts[ProspectStage.PROPOSAL_PRESENTATION] || 0)}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
          <CardHeader className='pb-3'>
            <CardTitle className='text-sm font-medium text-brand-gray flex items-center justify-between'>
              Total
              <FileText className='h-5 w-5 text-brand-gray' />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {prospectsQuery.isLoading ? (
              <Skeleton className='h-8 w-20' />
            ) : (
              <div className='text-3xl font-bold text-brand-navy'>{pagination?.total || 0}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters — shared by the table and kanban views, so they live outside
          the DataTable toolbar but write into its state. */}
      <Card className='border-gray-200 shadow-sm mb-6'>
        <CardContent className='pt-6'>
          <div className='flex flex-col md:flex-row gap-4'>
            <div className='flex-1 relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
              <Input
                placeholder='Search by company name...'
                value={table.search}
                onChange={(e) => table.setSearch(e.target.value)}
                className='pl-10 border-gray-200'
              />
            </div>
            <div className='w-full md:w-48'>
              <Select
                value={stageFilter || 'ALL'}
                onValueChange={(v) => table.setFilter('stage', v === 'ALL' ? '' : v)}
              >
                <SelectTrigger className='border-gray-200'>
                  <SelectValue placeholder='All Stages' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='ALL'>All Stages</SelectItem>
                  {PROSPECT_STAGE_ORDER.filter((s) => s !== ProspectStage.CONVERTED).map(
                    (stage) => (
                      <SelectItem key={stage} value={stage}>
                        {PROSPECT_STAGE_DISPLAY_NAMES[stage]}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
            {isInternalUser && (
              <div className='w-full md:w-48'>
                <Select
                  value={clientFilter || 'ALL'}
                  onValueChange={(v) => table.setFilter('client', v === 'ALL' ? '' : v)}
                >
                  <SelectTrigger className='border-gray-200'>
                    <SelectValue placeholder='All Clients' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='ALL'>All Clients</SelectItem>
                    {clientsQuery.data?.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <ExportCsvButton refetch={exportCsvQuery.refetch} filenamePrefix='prospects' />
          </div>
        </CardContent>
      </Card>

      {/* Table View */}
      {viewMode === 'table' ? (
        <>
          <DataTable<ProspectRow, Filters>
            table={table}
            columns={columns}
            data={prospectsQuery.data?.data}
            total={pagination?.total ?? 0}
            totalPages={pagination?.totalPages ?? 0}
            loading={prospectsQuery.isLoading}
            error={prospectsQuery.error}
            emptyMessage={
              hasActiveFilters
                ? 'No prospects found — try adjusting your search or filters'
                : 'No prospects found — get started by adding your first prospect'
            }
            title='Prospects'
            entity={{singular: 'prospect', plural: 'prospects'}}
            rowKey={(prospect) => prospect.id}
          />
          {!prospectsQuery.isLoading &&
            !prospectsQuery.error &&
            data.length === 0 &&
            !hasActiveFilters && (
              <div className='text-center mt-4'>
                <Button asChild className='bg-brand-blue hover:bg-brand-blue-dark text-white'>
                  <Link to={createUrl}>
                    <Plus className='h-4 w-4 mr-2' />
                    Add First Prospect
                  </Link>
                </Button>
              </div>
            )}
        </>
      ) : (
        /* Kanban View */
        <div className='-mx-2'>
          <div className='flex gap-4 overflow-x-auto px-2 pb-2'>
            {KANBAN_STAGES.map((stage) => {
              const items = groupedByStage[stage] || [];
              if (stageFilter && stage !== stageFilter) return null;
              return (
                <div key={stage} className='min-w-[320px] w-[320px]'>
                  <Card className='h-full border-gray-200 shadow-sm'>
                    <CardHeader className='py-3 bg-gray-50 border-b'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                          {getProspectStageIcon(stage)}
                          <CardTitle className='text-base font-semibold text-brand-navy'>
                            {PROSPECT_STAGE_DISPLAY_NAMES[stage]}
                          </CardTitle>
                        </div>
                        <Badge variant={getProspectStageColor(stage)}>{items.length}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent
                      className='space-y-3 p-4 min-h-[200px]'
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const prospectId = e.dataTransfer.getData('prospectId');
                        const prospectName = e.dataTransfer.getData('prospectName');
                        const fromStage = e.dataTransfer.getData('fromStage');
                        if (prospectId && fromStage !== stage) {
                          const fromIndex = PROSPECT_STAGE_ORDER.indexOf(
                            fromStage as ProspectStage,
                          );
                          const toIndex = PROSPECT_STAGE_ORDER.indexOf(stage);
                          if (toIndex > fromIndex) {
                            setKanbanDragTarget({
                              prospectId,
                              prospectName,
                              targetStage: stage,
                            });
                          }
                        }
                      }}
                    >
                      {items.length === 0 ? (
                        <div className='text-sm text-brand-gray py-6 text-center'>No items</div>
                      ) : (
                        items.map((prospect) => (
                          <div
                            key={prospect.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('prospectId', prospect.id);
                              e.dataTransfer.setData('prospectName', prospect.companyName);
                              e.dataTransfer.setData('fromStage', prospect.stage);
                            }}
                            className='border border-gray-200 rounded-md p-3 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer'
                            onClick={() => navigate(getDetailUrl(prospect.id))}
                          >
                            <div className='font-medium leading-snug text-brand-navy'>
                              {prospect.companyName}
                            </div>
                            {isInternalUser && (
                              <div className='text-xs text-brand-gray mt-1'>
                                {prospect.client.name}
                              </div>
                            )}
                            <div className='flex items-center justify-between mt-3'>
                              <div className='flex items-center gap-2 text-xs text-brand-gray'>
                                {prospect.estimatedFtEmployees != null && (
                                  <span>{prospect.estimatedFtEmployees} FT</span>
                                )}
                                {prospect.assignedBroker && (
                                  <span className='truncate max-w-[100px]'>
                                    {prospect.assignedBroker.name}
                                  </span>
                                )}
                              </div>
                              <div className='text-xs text-brand-gray'>
                                {getDaysInStage(prospect as never)}d
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Kanban Drag Confirmation */}
      <AlertDialog open={!!kanbanDragTarget} onOpenChange={() => setKanbanDragTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move Prospect</AlertDialogTitle>
            <AlertDialogDescription>
              Move <strong>{kanbanDragTarget?.prospectName}</strong> to{' '}
              <strong>
                {kanbanDragTarget?.targetStage
                  ? PROSPECT_STAGE_DISPLAY_NAMES[kanbanDragTarget.targetStage]
                  : ''}
              </strong>
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (kanbanDragTarget) {
                  updateStageMutation.mutate({
                    prospectId: kanbanDragTarget.prospectId,
                    stage: kanbanDragTarget.targetStage,
                  });
                }
                setKanbanDragTarget(null);
              }}
            >
              Move
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
